import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useOutletContext } from "react-router-dom";
import { importAvecReprise } from "../chargement";
import Chargement from "./Chargement";
import ErreurChargement from "./ErreurChargement";
import RequireAuth from "./RequireAuth";
import RequireRole from "./RequireRole";
import LayoutPrincipal from "./LayoutPrincipal";
import ListeCommandes from "./ListeCommandes";
import Page404 from "./Page404";

const PageConnexion = lazy(() => importAvecReprise(() => import("./auth/PageConnexion")));
const PageInscription = lazy(() => importAvecReprise(() => import("./auth/PageInscription")));
const PageMotDePasseOublie = lazy(() => importAvecReprise(() => import("./auth/PageMotDePasseOublie")));
const PageResetMotDePasse = lazy(() => importAvecReprise(() => import("./auth/PageResetMotDePasse")));
const PageReglages = lazy(() => importAvecReprise(() => import("./PageReglages")));
const PageMonCompte = lazy(() => importAvecReprise(() => import("./PageMonCompte")));
const GestionUtilisateurs = lazy(() => importAvecReprise(() => import("./GestionUtilisateurs")));
const ListeArchives = lazy(() => importAvecReprise(() => import("./ListeArchives")));
const PageGestion = lazy(() => importAvecReprise(() => import("./PageGestion")));
const ModalDetailCommande = lazy(() => importAvecReprise(() => import("./ModalDetailCommande")));
const NouvelleCommandeModal = lazy(() => importAvecReprise(() => import("./NouvelleCommandeModal")));
const ModalDetailUtilisateur = lazy(() => importAvecReprise(() => import("./ModalDetailUtilisateur")));
const ValiderPlan = lazy(() => importAvecReprise(() => import("./ValiderPlan")));

// Chaque page à la demande a sa propre zone d'attente : la liste autour
// reste affichée pendant qu'une fenêtre de détail arrive. Si le morceau ne
// charge jamais, ErreurChargement affiche un bouton pour recharger au lieu
// de laisser l'appli vide.
function A({ children, superpose }) {
  const attente = superpose ? (
    <div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(17,24,39,0.25)", zIndex: 1000 }}>
      <Chargement />
    </div>
  ) : (
    <Chargement />
  );
  return (
    <ErreurChargement>
      <Suspense fallback={attente}>{children}</Suspense>
    </ErreurChargement>
  );
}

export default function AppRouter({ session, profil, sessionLoading, profilLoading, onProfilUpdate }) {
  const dejaConnecte = !!session && !!profil && profil.statut === "actif";

  return (
    <Routes>
      <Route path="/connexion" element={dejaConnecte ? <Navigate to="/commandes" replace /> : <A><PageConnexion /></A>} />
      <Route path="/inscription" element={dejaConnecte ? <Navigate to="/commandes" replace /> : <A><PageInscription /></A>} />
      <Route path="/mot-de-passe-oublie" element={dejaConnecte ? <Navigate to="/commandes" replace /> : <A><PageMotDePasseOublie /></A>} />
      <Route path="/reset-mot-de-passe" element={<A><PageResetMotDePasse /></A>} />
      {/* Le lien « Je valide le plan » des emails : public, le client n'a pas de
          compte. Pas de redirection si l'on est connecte : Simon doit pouvoir
          l'ouvrir pour essayer. */}
      <Route path="/valider/:code" element={<A><ValiderPlan /></A>} />

      <Route element={<RequireAuth session={session} profil={profil} sessionLoading={sessionLoading} profilLoading={profilLoading} />}>
        <Route element={<LayoutPrincipal session={session} profil={profil} onProfilUpdate={onProfilUpdate} />}>
          <Route index element={<Navigate to="/commandes" replace />} />
          <Route path="/commandes/archives" element={<A><ListeArchives /></A>}>
            <Route path=":ref" element={<A superpose><ModalDetailCommande retour="/commandes/archives" /></A>} />
          </Route>
          <Route path="/commandes" element={<ListeCommandes />}>
            <Route path="nouvelle" element={<A superpose><NouvelleCommandeModal /></A>} />
            <Route path=":ref" element={<A superpose><ModalDetailCommande /></A>} />
          </Route>
          <Route path="/reglages" element={<A><PageReglagesWrapper /></A>} />
          <Route path="/mon-compte" element={<A><PageMonCompteWrapper /></A>} />

          <Route element={<RequireRole profil={profil} roles={["admin", "utilisateur"]} requireOwner />}>
            <Route path="/utilisateurs" element={<A><GestionUtilisateurs /></A>}>
              <Route path=":uid" element={<A superpose><ModalDetailUtilisateur /></A>} />
            </Route>
            <Route path="/gestion" element={<A><PageGestion /></A>} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Page404 />} />
    </Routes>
  );
}

function PageReglagesWrapper() {
  const { profil, onProfilUpdate } = useOutletContext();
  return <PageReglages profil={profil} onProfilUpdate={onProfilUpdate} />;
}

function PageMonCompteWrapper() {
  const { session, profil, onProfilUpdate, commandes } = useOutletContext();
  // PageMonCompte takes a `role` prop with values "dessinateur" or "utilisateur".
  // The legacy code maps admin and utilisateur both to "utilisateur" (cf VueUtilisateur.js:517).
  const roleProp = profil.role === "dessinateur" ? "dessinateur" : "utilisateur";
  return <PageMonCompte profil={profil} session={session} role={roleProp} commandes={commandes} onProfilUpdate={onProfilUpdate} />;
}

